"use client";

import { type ReactNode, useLayoutEffect, useRef } from "react";

const minPx = 14;

/** Long words shrink to fit the heading without breaking inside the name. */
export function FitTitle({
  children,
  ...rest
}: { readonly children: ReactNode } & {
  readonly className?: string;
  readonly style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLHeadingElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;

    if (!el) return;

    const fit = () => {
      el.style.fontSize = "";
      let size = parseFloat(getComputedStyle(el).fontSize);

      while (el.scrollWidth > el.clientWidth + 0.5 && size > minPx) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    // Name edits can change the text without changing the observed box.
    const mutations = new MutationObserver(fit);
    mutations.observe(el, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, []);
  return (
    <h1
      ref={ref}
      {...rest}
      style={{
        ...rest.style,
        textWrap: "balance",
        overflowWrap: "normal",
        wordBreak: "normal",
        hyphens: "none",
      }}
    >
      {children}
    </h1>
  );
}
